import { queryOptions } from '@tanstack/react-query'
import { QuoteBuilderApiError, type QuoteBuilderApi } from '../api/client'
import type { Quote } from '../api/types'
import type {
  AqprojImportResult,
  CatalogMatch,
  DocumentImport,
  ImportEvent,
  ImportMode,
  ImportSelection,
  ProductSuggestions,
  ImportReviewUpdate,
  UnfinishedImport,
} from './types'

// Import requests (demo `features/imports/api.ts`). Files go to the API as multipart uploads.

const readFailure = 'Could not read the equipment list. Try again.'

/** Upload a document for import; the read starts with `streamImportExtraction`. */
export function createImport(api: QuoteBuilderApi, file: File, mode: ImportMode, quoteId: string) {
  const body = new FormData()
  body.append('file', file)
  body.append('mode', mode)
  body.append('quote_id', quoteId)
  return api.request<DocumentImport>('/document-imports', { method: 'POST', body })
}

/** Start an import from a file already in the quote's Files. */
export function createImportFromQuoteFile(api: QuoteBuilderApi, fileId: string, quoteId: string, mode: ImportMode = 'equipment_schedule') {
  return api.request<DocumentImport>(`/quote-files/${fileId}/document-import`, {
    method: 'POST',
    body: JSON.stringify({ quote_id: quoteId, mode }),
  })
}

export function readImport(api: QuoteBuilderApi, importId: string, signal?: AbortSignal) {
  return api.request<DocumentImport>(`/document-imports/${importId}`, { signal })
}

/** The imported document itself, for the Document view of a reopened review. */
export async function readImportFile(api: QuoteBuilderApi, documentImport: Pick<DocumentImport, 'id' | 'original_file_name' | 'content_type'>) {
  const response = await api.fetch(`/document-imports/${documentImport.id}/file`)
  if (!response.ok) throw new QuoteBuilderApiError(response.status, await response.json().catch(() => ({})))
  return new File([await response.blob()], documentImport.original_file_name, { type: documentImport.content_type })
}

/** Saves review choices; each row sent replaces that row's saved choices. */
export function saveImportReview(api: QuoteBuilderApi, importId: string, update: ImportReviewUpdate) {
  return api.request<void>(`/document-imports/${importId}/review`, { method: 'PATCH', body: JSON.stringify(update) })
}

export function discardImport(api: QuoteBuilderApi, importId: string) {
  return api.request<void>(`/document-imports/${importId}`, { method: 'DELETE' })
}

/** Your imports into this quote that are still being read or reviewed. */
export const unfinishedImportsQueryOptions = (api: QuoteBuilderApi, quoteId: string) =>
  queryOptions({
    queryKey: ['quote-unfinished-imports', quoteId],
    queryFn: ({ signal }) => api.request<UnfinishedImport[]>(`/quotes/${quoteId}/document-imports`, { signal }),
    staleTime: 0,
  })

/** Reads the document live: rows, catalog steps and matches arrive as events before the result. */
export async function streamImportExtraction(
  api: QuoteBuilderApi,
  importId: string,
  onEvent: (event: ImportEvent) => void,
  signal?: AbortSignal,
) {
  const response = await api.fetch(`/document-imports/${importId}/extraction`, {
    method: 'POST',
    signal,
    headers: { Accept: 'text/event-stream' },
  })
  if (!response.ok || !response.body) {
    const body = await response.json().catch(() => ({}))
    throw new QuoteBuilderApiError(response.status, body)
  }
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffered = ''
  try {
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      buffered += decoder.decode(value, { stream: true })
      let boundary: number
      while ((boundary = buffered.indexOf('\n\n')) !== -1) {
        const chunk = buffered.slice(0, boundary)
        buffered = buffered.slice(boundary + 2)
        if (!chunk.startsWith('data: ')) continue
        const event = JSON.parse(chunk.slice(6)) as ImportEvent
        if (event.type === 'result') return event.import
        if (event.type === 'error') throw new QuoteBuilderApiError(event.status, { detail: { code: event.code } })
        onEvent(event)
      }
    }
  } finally {
    await reader.cancel().catch(() => undefined)
  }
  throw new Error(readFailure)
}

export async function searchCatalog(api: QuoteBuilderApi, query: string, signal?: AbortSignal) {
  const search = new URLSearchParams({ query, page_size: '6', include_facets: 'false' })
  const page = await api.request<{ items: CatalogMatch[] }>(`/catalog/products?${search}`, { signal })
  return page.items
}

/** The options and accessories the import suggests for a row once Sales picks this product. */
export function rowSuggestions(api: QuoteBuilderApi, importId: string, rowId: string, productId: number, signal?: AbortSignal) {
  return api.request<ProductSuggestions>(
    `/document-imports/${importId}/rows/${encodeURIComponent(rowId)}/suggestions?product_id=${productId}`,
    { signal },
  )
}

export function insertImport(
  api: QuoteBuilderApi,
  documentImport: DocumentImport,
  quote: Pick<Quote, 'id' | 'revision'>,
  selections: ImportSelection[],
) {
  return api.request<Quote>(`/document-imports/${documentImport.id}/insert`, {
    method: 'POST',
    body: JSON.stringify({ quote_id: quote.id, expected_revision: quote.revision, selections }),
  })
}

/** An AQProj file becomes a new quote. */
export function importAqproj(api: QuoteBuilderApi, file: File) {
  const body = new FormData()
  body.append('file', file)
  return api.request<AqprojImportResult>('/aqproj-imports', { method: 'POST', body })
}

export function importErrorMessage(error: unknown, fallback: string) {
  if (!(error instanceof QuoteBuilderApiError)) return fallback
  switch (error.code) {
    case 'stale_revision':
      return 'The quote changed while adding equipment. Review your selections and try again.'
    case 'unsupported_import_file':
      return 'Choose a PDF, XLSX, CSV, PNG or JPG file.'
    case 'import_file_too_large':
      return 'Choose a file smaller than 25 MB.'
    case 'document_import_not_found':
      return 'This import was discarded. Upload the file again.'
    case 'aqproj_decode_failed':
      return 'This is not a readable AutoQuotes project file.'
    case 'aqproj_has_no_lines':
      return 'This AutoQuotes project has no items.'
    case 'import_row_quantity_not_whole':
      return 'An approved item has a quantity that is not a whole number. Reject it and add it by hand.'
    case 'invalid_product_option':
    case 'unavailable_product_option':
      return 'A chosen option is no longer offered. Choose the options again and retry.'
    case 'invalid_accessory':
      return 'An added accessory is no longer offered for its product. Remove it and retry.'
    default:
      return fallback
  }
}
